/**
 * Detalhe do Pedido — /pedidos/:id (cockpit de produção, mockup Fase 2).
 *
 * Layout fiel ao mockup:
 *   breadcrumb · .eh (situação + gargalo + ações) · .kpi-strip-v2
 *   (Valor/Margem/Lead time/OPs/Faturado) · .steps (5 passos) · .estacoes
 *   (timeline do roteiro) · .row-2col (OPs vinculadas + ambientes | Eventos +
 *   identificação/cliente/datas/revisões).
 *
 * Preserva 100% da máquina existente: revisão (iniciar/finalizar/cancelar),
 * gerar OPs, transições (produção/expedição/entrega), cancelar, etiqueta de
 * expedição e o EditorPedidoAmbientes (editável em revisão/RECEBIDO).
 *
 * Dados: GET /api/pedidos/{id} (completo, p/ editor+ações) + /cockpit
 * (KPIs/steps/estações/OPs/eventos) + /pedido-revisoes.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { dataLocalDe } from "../../utils/dinheiro";
import {
  FiAlertCircle, FiCheck, FiEdit3, FiMessageCircle, FiPackage, FiPlay,
  FiPrinter, FiTruck, FiX, FiXCircle, FiZap,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import EntityHeader from "../../components/EntityHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { abrirEtiquetaExpedicaoEmNovaAba } from "../../services/producao";
import {
  cancelarPedido, cancelarRevisao, finalizarRevisao, gerarOpsDoPedido,
  liberarOpsDoPedido,
  iniciarExpedicao, iniciarProducao, iniciarRevisao, listarRevisoes,
  marcarEntregue, obterCockpitPedido, obterPedido,
} from "../../services/pedidos";
import { PageContainer } from "../../styles/shared";

import CronogramaPagamento from "./CronogramaPagamento";
import EditorPedidoAmbientes from "./EditorPedidoAmbientes";
import {
  fmtData, fmtMoeda, PRIORIDADE_TONE, SITUACAO_TONE_PEDIDO,
} from "../Orcamentos/helpers";
import {
  Block, DangerButton, EmptyMsg, ErrorMsg, FieldLabel, FieldRow, FieldValue,
  Form, FormFooter, InlineHint, Pill, PrimaryButton, SecondaryButton,
  SectionTitle, Textarea, WarningMsg,
} from "./styles";

// ---------- Helpers ----------

function fmtCompacto(v) {
  const n = Number(v || 0);
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  if (n >= 1_000) return `R$ ${Math.round(n / 1_000)}k`;
  return `R$ ${Math.round(n)}`;
}
function fmtDataCurta(iso) {
  if (!iso) return null;
  try { return (dataLocalDe(iso) || new Date(NaN)).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }); }
  catch { return null; }
}
function relativoDias(iso) {
  if (!iso) return null;
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const dias = Math.round((d - hoje) / 86400000);
  if (Number.isNaN(dias)) return null;
  if (dias === 0) return "hoje";
  if (dias > 0) return `em ${dias} dia${dias === 1 ? "" : "s"}`;
  return `há ${Math.abs(dias)} dia${dias === -1 ? "" : "s"}`;
}
function whatsappUrl(tel) {
  const d = (tel || "").replace(/\D/g, "");
  return d ? `https://wa.me/${d}` : null;
}

function KpiV2({ label, value, unit, meta, metaTone }) {
  return (
    <div className="kpi-v2">
      <div className="lbl">{label}</div>
      <div className="val">{value}{unit ? <span className="unit">{unit}</span> : null}</div>
      <div className="meta" style={metaTone ? { color: metaTone } : undefined}>{meta}</div>
    </div>
  );
}

function PedidoDetalhe() {
  const navigate = useNavigate();
  const { pedidoId } = useParams();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const toast = useToast();

  const [estado, setEstado] = useState({ loading: true, error: null, pedido: null });
  const [cockpit, setCockpit] = useState(null);
  const [revisoes, setRevisoes] = useState([]);
  const [confirmando, setConfirmando] = useState(null);
  // M-PED-6: o PCP decide se a peça vai direto para o chão ou espera
  // conferência. O backend aceita `liberar` desde a Fase 8.4; a tela
  // nunca perguntou, então toda OP nascia liberada.
  const [liberarAoGerar, setLiberarAoGerar] = useState(true);
  const [motivo, setMotivo] = useState("");
  // Cancelar o pedido deve poder parar a produção junto (ALTA-10).
  const [cancelarProducao, setCancelarProducao] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [acaoErro, setAcaoErro] = useState(null);

  const podeEditar = isMaster || hasAllPermissions(["pedido:edit"]);
  const podeCancelar = isMaster || hasAllPermissions(["pedido:cancel"]);
  const podeRevisar = isMaster || hasAllPermissions(["pedido:revisar"]);
  const podeGerarOps = isMaster || hasAllPermissions(["ordem_producao:gerar_ops"]);
  // Liberar tem gate proprio no backend (`ordem_producao:liberar`): quem gera
  // nao necessariamente e quem autoriza cortar. Usar `podeGerarOps` aqui
  // mostraria um botao que responde 403 no clique.
  const podeLiberarOps = isMaster || hasAllPermissions(["ordem_producao:liberar"]);
  const podeVerOps = isMaster || hasAllPermissions(["ordem_producao:view"]);

  const carregar = useCallback(async () => {
    setEstado({ loading: true, error: null, pedido: null });
    try {
      const [p, ck] = await Promise.all([
        obterPedido(request, pedidoId),
        obterCockpitPedido(request, pedidoId, { silentError: true }).catch(() => null),
      ]);
      setEstado({ loading: false, error: null, pedido: p });
      setCockpit(ck);
      const revPayload = await listarRevisoes(request, pedidoId);
      const items = revPayload?.results || revPayload || [];
      setRevisoes(Array.isArray(items) ? items : []);
    } catch (err) {
      setEstado({ loading: false, error: err?.message || "Erro ao carregar.", pedido: null });
    }
  }, [request, pedidoId]);

  useEffect(() => { carregar(); }, [carregar]);

  const p = estado.pedido;

  const editavel = useMemo(() => {
    if (!p || !podeRevisar) return false;
    if (p.situacao === "ENTREGUE" || p.situacao === "CANCELADO") return false;
    return p.revisao_em_andamento || p.situacao === "RECEBIDO";
  }, [p, podeRevisar]);

  // ---------- Ações (preservadas) ----------
  const abrir = (tipo) => { setConfirmando(tipo); setMotivo(""); setAcaoErro(null); };
  const fechar = () => { setConfirmando(null); setMotivo(""); setAcaoErro(null); };

  const executar = async () => {
    if (!p) return;
    setSalvando(true); setAcaoErro(null);
    try {
      if (confirmando === "iniciar-producao") await iniciarProducao(request, p.id);
      else if (confirmando === "gerar-ops") {
        await gerarOpsDoPedido(request, p.id, { liberar: liberarAoGerar });
      }
      else if (confirmando === "liberar-ops") await liberarOpsDoPedido(request, p.id);
      else if (confirmando === "iniciar-expedicao") await iniciarExpedicao(request, p.id);
      else if (confirmando === "marcar-entregue") await marcarEntregue(request, p.id);
      else if (confirmando === "cancelar") {
        if (!motivo.trim()) { setAcaoErro("Motivo é obrigatório."); setSalvando(false); return; }
        await cancelarPedido(request, p.id, motivo, { cancelarOps: cancelarProducao });
      } else if (confirmando === "iniciar-revisao") {
        if (!motivo.trim()) { setAcaoErro("Motivo eh obrigatório."); setSalvando(false); return; }
        await iniciarRevisao(request, p.id, motivo);
      } else if (confirmando === "finalizar-revisao") await finalizarRevisao(request, p.id);
      else if (confirmando === "cancelar-revisao") await cancelarRevisao(request, p.id);
      await carregar();
      fechar();
    } catch (err) {
      setAcaoErro(err?.message || "Erro na operação.");
    } finally {
      setSalvando(false);
    }
  };

  const imprimirEtiquetaExpedicao = useCallback(async () => {
    if (!p) return;
    try {
      await abrirEtiquetaExpedicaoEmNovaAba(request, p.id, {});
      toast.success("Etiqueta de expedição gerada.");
    } catch (err) {
      if (err?.reimpressao) {
        const m = window.prompt("Etiqueta de expedição deste pedido já foi impressa. Informe motivo:", "");
        if (!m || !m.trim()) { toast.error("Reimpressão cancelada (motivo vazio)."); return; }
        try {
          await abrirEtiquetaExpedicaoEmNovaAba(request, p.id, { motivo: m.trim() });
          toast.success("Etiqueta reimpressa.");
        } catch (err2) { toast.error(err2?.message || "Falha ao reimprimir."); }
      } else {
        toast.error(err?.message || "Falha ao gerar etiqueta.");
      }
    }
  }, [p, request, toast]);

  // Produção v2 conduz o pedido (auditoria 2026-08-07, CRIT-4/ALTA-11): os
  // ambientes nascem na conversão e andam pelo Painel de Produção. O fluxo
  // v1 ("Gerar OPs" a partir do BOM CAD) só aparece em pedido que NÃO tem
  // ambiente v2 — manter os dois vivos lado a lado fazia a tela do pedido e
  // a da fábrica contarem histórias opostas sobre o mesmo trabalho.
  // Fail-CLOSED: enquanto o cockpit não respondeu (ou falhou), NÃO assumir
  // que o pedido é do fluxo v1 — assumir isso reabria "Gerar OPs" num pedido
  // que já está na produção v2 e recriava as duas verdades. `ambientes_producao`
  // vem do próprio pedido e serve de confirmação independente.
  const cockpitPendente = cockpit === null;
  const usaProducaoV2 = Boolean(
    cockpit?.producao_v2?.usa_v2 || (p?.ambientes_producao_count || 0) > 0,
  );

  // Transição principal contextual (vira botão visível no header).
  const mainTransition = useMemo(() => {
    if (!p || p.revisao_em_andamento) return null;
    if (!usaProducaoV2 && !cockpitPendente && podeGerarOps && p.situacao === "RECEBIDO" && !(p.ops_count || 0)) {
      // So oferece "Gerar OPs" quando ha BOM CAD. Sem BOM (orcamento manual),
      // o botao so falharia -> escondemos e mostramos hint pra importar.
      if (cockpit?.pedido && cockpit.pedido.tem_bom_cad === false) return null;
      return { key: "gerar-ops", label: "Gerar OPs", icon: <FiPlay size={14} />, onClick: () => abrir("gerar-ops") };
    }
    // M-PED-6: OPs geradas em rascunho esperam a conferencia do PCP. Sem
    // este botao, o `liberar=false` seria um beco — a peca ficaria parada
    // sem caminho de volta para o chao.
    if (!usaProducaoV2 && podeLiberarOps && p.situacao === "RECEBIDO" && (p.ops_rascunho_count || 0) > 0) {
      return {
        key: "liberar-ops",
        label: `Liberar ${p.ops_rascunho_count} peça(s) para o chão`,
        icon: <FiPlay size={14} />,
        onClick: () => abrir("liberar-ops"),
      };
    }
    if (podeEditar && p.situacao === "EM_PRODUCAO")
      return { key: "iniciar-expedicao", label: "Iniciar expedição", icon: <FiPackage size={14} />, onClick: () => abrir("iniciar-expedicao") };
    if (podeEditar && p.situacao === "EM_EXPEDICAO")
      return { key: "marcar-entregue", label: "Marcar entregue", icon: <FiTruck size={14} />, onClick: () => abrir("marcar-entregue") };
    return null;
  }, [p, podeEditar, podeGerarOps, podeLiberarOps, cockpit, usaProducaoV2, cockpitPendente]);

  const primaryAction = useMemo(() => {
    if (!p) return null;
    if (p.revisao_em_andamento && podeRevisar)
      return { label: "Finalizar revisão", icon: <FiCheck size={14} />, onClick: () => abrir("finalizar-revisao") };
    return mainTransition;
  }, [p, podeRevisar, mainTransition]);

  const actionsKebab = useMemo(() => {
    if (!p) return [];
    const out = [];
    const tel = cockpit?.pedido?.cliente_telefone;
    if (tel) out.push({ label: "Conversar (WhatsApp)", icon: <FiMessageCircle size={14} />, onClick: () => window.open(whatsappUrl(tel), "_blank", "noopener"), key: "wa" });
    if (podeRevisar && p.situacao === "RECEBIDO" && !p.revisao_em_andamento)
      out.push({ label: "Iniciar revisão", icon: <FiEdit3 size={14} />, onClick: () => abrir("iniciar-revisao"), key: "rev-start" });
    if (podeEditar && p.situacao === "RECEBIDO" && !p.revisao_em_andamento && (p.ops_count || 0) > 0)
      out.push({ label: "Iniciar produção", icon: <FiPlay size={14} />, onClick: () => abrir("iniciar-producao"), key: "prod" });
    if (p.situacao === "EM_PRODUCAO" || p.situacao === "EM_EXPEDICAO")
      out.push({ label: "Imprimir etiqueta expedição", icon: <FiPrinter size={14} />, onClick: () => imprimirEtiquetaExpedicao(), key: "etiq-exp" });
    if (podeCancelar && p.situacao !== "ENTREGUE" && p.situacao !== "CANCELADO")
      out.push({ label: "Cancelar pedido", icon: <FiXCircle size={14} />, onClick: () => abrir("cancelar"), danger: true, key: "canc" });
    // remove o que virou ação primária visível
    return out.filter((a) => a.key !== (primaryAction && mainTransition && mainTransition.key));
  }, [p, cockpit, podeEditar, podeCancelar, podeRevisar, imprimirEtiquetaExpedicao, primaryAction, mainTransition]);

  if (estado.loading) {
    return <PageContainer><EmptyMsg>Carregando pedido...</EmptyMsg></PageContainer>;
  }
  if (estado.error || !p) {
    return (
      <PageContainer>
        <ErrorMsg>{estado.error || "Pedido não encontrado."}</ErrorMsg>
        <SecondaryButton type="button" onClick={() => navigate("/pedidos")}>Voltar para a lista</SecondaryButton>
      </PageContainer>
    );
  }

  const ck = cockpit;
  const kp = ck?.kpis;
  const cab = ck?.pedido;
  const gargalo = ck?.gargalo;

  const numeroExibido = p.numero_revisao > 0
    ? `${p.numero} · r${p.numero_revisao}${p.revisao_em_andamento ? " (em andamento)" : ""}`
    : p.numero;

  // Badges: situação + gargalo + prioridade
  const badges = [
    { label: p.situacao_display, tone: SITUACAO_TONE_PEDIDO[p.situacao] || "neutral", key: "sit" },
  ];
  if (gargalo) {
    badges.push({
      label: `Gargalo: ${gargalo.label}${gargalo.horas != null ? ` · ${gargalo.horas}h` : ""}`,
      tone: "warning", key: "garg", icon: <FiZap size={12} aria-hidden="true" />,
    });
  }
  badges.push({ label: p.prioridade_display, tone: PRIORIDADE_TONE[p.prioridade] || "neutral", key: "pri" });

  // Subtítulo composto (mockup): aprovado · valor · margem · entrega prevista
  const subParts = [];
  if (cab?.aprovado_em) subParts.push(`Aprovado ${fmtDataCurta(cab.aprovado_em)}`);
  subParts.push(fmtMoeda(p.valor_total));
  if (cab?.margem != null) subParts.push(`margem ${Number(cab.margem).toFixed(1)}%`);
  if (p.data_entrega_prevista) {
    const rel = relativoDias(p.data_entrega_prevista);
    subParts.push(`entrega ${fmtDataCurta(p.data_entrega_prevista)}${rel ? ` · ${rel}` : ""}`);
  }

  const estCols = ck?.estacoes?.length || 6;

  return (
    <PageContainer style={{ maxWidth: "none" }}>
      <div className="breadcrumb" style={{ marginBottom: 8 }}>
        <span className="crumb">Início</span><span className="crumb-sep">/</span>
        <span className="crumb">Comercial</span><span className="crumb-sep">/</span>
        <span className="crumb">Pedidos</span><span className="crumb-sep">/</span>
        <span className="crumb active">{p.numero}</span>
      </div>

      <EntityHeader
        onBack={() => navigate("/pedidos")}
        backLabel="Lista"
        title={numeroExibido}
        subtitle={`${p.cliente_nome} · ${subParts.join(" · ")}`}
        badges={badges}
        primaryAction={primaryAction || undefined}
        actions={actionsKebab}
      />

      {/* KPIs */}
      <div className="kpi-strip-v2">
        <KpiV2 label="Valor" value={fmtCompacto(p.valor_total)}
          meta={kp?.valor_delta_pct != null ? `${kp.valor_delta_pct >= 0 ? "▲" : "▼"} ${Math.abs(Math.round(kp.valor_delta_pct))}% vs médio` : "ticket"} />
        <KpiV2 label="Margem"
          value={cab?.margem != null ? Number(cab.margem).toFixed(1) : "—"}
          unit={cab?.margem != null ? "%" : undefined}
          meta={kp?.margem_label || "—"} />
        <KpiV2 label="Lead time"
          value={kp?.lead_time_dias != null ? kp.lead_time_dias : "—"}
          unit={kp?.lead_time_dias != null ? "d" : undefined}
          meta={kp?.lead_time_medio != null ? `média ${kp.lead_time_medio}d` : "em curso"} />
        <KpiV2 label="OPs"
          value={kp ? `${kp.ops_concluidas}/${kp.ops_total}` : "—"}
          meta={kp && kp.ops_total ? `${kp.ops_total - kp.ops_concluidas} pendentes` : "sem OPs"} />
        <KpiV2 label="Faturado"
          value={kp ? Math.round(kp.faturado_pct) : 0} unit="%"
          meta={kp ? `${fmtCompacto(kp.faturado_valor)} · ${kp.faturado_nfe} NF-e` : "—"} />
      </div>

      {/* Stepper */}
      {ck?.steps?.length > 0 && (
        <div className="steps" style={{ marginTop: 14 }}>
          {ck.steps.map((s, i) => (
            <div key={s.chave} className={`steps__step ${s.status}`}>
              <div className="steps__num">{s.status === "is-done" ? <FiCheck size={12} aria-hidden="true" /> : i + 1}</div>
              <div className="steps__txt">
                <div className="lbl">{s.label}</div>
                <div className="ttl" title={s.titulo}>{s.titulo}</div>
                <div className="sub">{s.sub}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Timeline de estações (quando há produção) */}
      {ck?.estacoes?.length > 0 && (kp?.ops_total || 0) > 0 && (
        <div className="estacoes" style={{ marginTop: 14 }}>
          <div className="estacoes__head">
            <div className="estacoes__title">Roteiro de produção</div>
            <div className="estacoes__legend">
              <span><i style={{ background: "var(--success-solid)" }} />Concluído</span>
              <span><i style={{ background: "var(--accent)" }} />Em curso</span>
              <span><i style={{ background: "var(--border)" }} />Pendente</span>
            </div>
          </div>
          <div className="estacoes__rail" style={{ "--estacoes-cols": estCols }}>
            {ck.estacoes.map((e, i) => (
              <div key={e.estacao} className={`estacoes__cell ${e.status}`}>
                <div className="estacoes__dot">{e.status === "done" ? <FiCheck size={13} aria-hidden="true" /> : i + 1}</div>
                <div className="estacoes__txt">
                  <div className="nm">
                    {e.label}
                    {e.status === "now" && gargalo?.estacao === e.estacao ? " ⚡" : ""}
                  </div>
                  <div className="ts">
                    {e.ts ? fmtDataCurta(e.ts)
                      : (e.status === "now" && gargalo?.horas != null ? `${gargalo.horas}h` : "")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {p.revisao_em_andamento && (
        <WarningMsg style={{ marginTop: 14 }}>
          <FiEdit3 size={14} aria-hidden="true" />
          Revisão r{p.numero_revisao} em andamento. Edite ambientes/itens e clique em
          "Finalizar revisão" quando terminar (ou "Cancelar revisão" para descartar).
        </WarningMsg>
      )}
      {p.situacao === "CANCELADO" && (
        <ErrorMsg style={{ marginTop: 14 }}>
          <FiXCircle size={14} aria-hidden="true" /> Cancelado
          {p.motivo_cancelamento && ` — ${p.motivo_cancelamento}`}
        </ErrorMsg>
      )}
      {/* O aviso "sem BOM CAD / não dá para gerar OPs" só faz sentido no
          fluxo v1. Com produção v2 o pedido JÁ está na fábrica (ambientes
          criados na conversão) — exibi-lo ali fazia a tela do vendedor dizer
          que a produção não recebeu nada (auditoria 2026-08-07, CRIT-4). */}
      {!usaProducaoV2 && !cockpitPendente && p.situacao === "RECEBIDO" && !(p.ops_count || 0)
        && cab?.tem_bom_cad === false && (
        <WarningMsg style={{ marginTop: 14 }}>
          <FiAlertCircle size={14} aria-hidden="true" />
          Pedido de orçamento manual — sem BOM CAD, não há peças para gerar Ordens de Produção
          automaticamente. Importe o projeto via DinaBox no orçamento de origem
          {p.orcamento_origem_numero ? ` (${p.orcamento_origem_numero})` : ""} para liberar a geração de OPs.
        </WarningMsg>
      )}

      {/* 2 colunas */}
      <div className="row-2col" style={{ marginTop: 14 }}>
        {/* Esquerda: produção (v2 por ambiente, ou OPs v1) + ambientes */}
        <div className="stack-14">
          {usaProducaoV2 && (
            <div className="block">
              <div className="block__head">
                <div className="block__title">
                  Produção
                  <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>
                    {" "}· {ck.producao_v2.carregados}/{ck.producao_v2.total} carregados
                  </span>
                </div>
                {podeVerOps && (
                  <button type="button" className="btn btn--ghost btn--sm"
                          onClick={() => navigate(`/producao/pedidos/${p.id}`)}>
                    Ver produção →
                  </button>
                )}
              </div>
              <div className="block__body" style={{ padding: 0 }}>
                <div className="table-wrap">
                  <table className="dtable">
                    <colgroup>
                      <col style={{ width: "26%" }} /><col />
                      <col style={{ width: "26%" }} /><col style={{ width: "18%" }} />
                    </colgroup>
                    <thead>
                      <tr><th>Ambiente</th><th>Nome</th><th>Fase</th><th>Etapas</th></tr>
                    </thead>
                    <tbody>
                      {ck.producao_v2.ambientes.map((a) => {
                        const pct = a.etapas_total
                          ? Math.round((a.etapas_concluidas / a.etapas_total) * 100) : 0;
                        return (
                          <tr key={a.numero}
                              onClick={() => podeVerOps && navigate(`/producao/pedidos/${p.id}`)}
                              style={{ cursor: podeVerOps ? "pointer" : "default" }}>
                            <td className="mono">{a.numero}</td>
                            <td title={a.nome}>{a.nome}</td>
                            <td>
                              <span className={"badge no-dot " + (a.cancelado ? "danger" : a.congelado ? "warning" : "info")}>
                                {a.cancelado ? "Cancelado" : a.congelado ? "Congelado" : a.fase_label}
                              </span>
                            </td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ flex: 1, height: 6, borderRadius: 999, background: "var(--surface-2)", overflow: "hidden" }}>
                                  <div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "var(--success-solid)" : "var(--accent)" }} />
                                </div>
                                <span className="tiny muted mono">{a.etapas_concluidas}/{a.etapas_total}</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {ck?.ops?.length > 0 && (
            <div className="block">
              <div className="block__head">
                <div className="block__title">
                  OPs vinculadas <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>· {ck.ops.length}</span>
                </div>
                {podeVerOps && (
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate(`/producao/pedidos/${p.id}`)}>
                    Ver producao →
                  </button>
                )}
              </div>
              <div className="block__body" style={{ padding: 0 }}>
                <div className="table-wrap">
                  <table className="dtable">
                    <colgroup><col style={{ width: "26%" }} /><col /><col style={{ width: "22%" }} /><col style={{ width: "22%" }} /></colgroup>
                    <thead><tr><th>OP</th><th>Ambiente</th><th>Estação atual</th><th>Progresso</th></tr></thead>
                    <tbody>
                      {ck.ops.map((o) => (
                        <tr key={o.id}
                          onClick={() => podeVerOps && navigate(`/producao/pedidos/${p.id}`)}
                          style={{ cursor: podeVerOps ? "pointer" : "default" }}>
                          <td className="mono">{o.numero}</td>
                          <td title={o.ambiente}>{o.ambiente}</td>
                          <td>{o.estacao_label !== "—"
                            ? <span className="badge info no-dot">{o.estacao_label}</span>
                            : <span className="muted">—</span>}</td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, height: 6, borderRadius: 999, background: "var(--surface-2)", overflow: "hidden" }}>
                                <div style={{ width: `${o.progresso}%`, height: "100%", background: o.progresso >= 100 ? "var(--success-solid)" : "var(--accent)" }} />
                              </div>
                              <span className="tiny muted mono">{o.progresso}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="block">
            <div className="block__head"><div className="block__title">Ambientes e itens</div></div>
            <div className="block__body">
              <EditorPedidoAmbientes pedido={p} editavel={editavel} onChange={carregar} />
              {p.revisao_em_andamento && podeRevisar && (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <PrimaryButton type="button" onClick={() => abrir("finalizar-revisao")}>
                    <FiCheck size={14} /> Finalizar revisão
                  </PrimaryButton>
                  <DangerButton type="button" onClick={() => abrir("cancelar-revisao")}>
                    <FiX size={14} /> Cancelar revisão (restaura)
                  </DangerButton>
                </div>
              )}
            </div>
          </div>

          {/* Tarefa F2: parcelas da condicao de pagamento com datas reais do
              processo. Some sozinho quando o pedido nao tem condicao. */}
          <CronogramaPagamento pedidoId={p.id} />
        </div>

        {/* Direita: Eventos + identificação/cliente/datas/revisões */}
        <div className="stack-14">
          {ck?.eventos?.length > 0 && (
            <div className="block">
              <div className="block__head"><div className="block__title">Eventos</div></div>
              <div className="block__body">
                <div className="stack" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {ck.eventos.map((e) => (
                    <div key={e.id} className="flex" style={{ gap: 10, alignItems: "baseline" }}>
                      <span className="tiny muted mono" style={{ minWidth: 40 }}>{e.rotulo}</span>
                      <div style={{ fontSize: 13, minWidth: 0 }}>
                        <strong className="mono">{e.op_numero}</strong>{" "}
                        {e.acao_label} · {e.estacao_label}
                        <div className="tiny muted">{e.operador}{e.nota ? ` · ${e.nota}` : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <Block>
            <SectionTitle>Identificação</SectionTitle>
            <FieldRow><FieldLabel>Número</FieldLabel><FieldValue>{p.numero}</FieldValue></FieldRow>
            <FieldRow><FieldLabel>Unidade</FieldLabel><FieldValue>{p.unidade_codigo}</FieldValue></FieldRow>
            <FieldRow>
              <FieldLabel>Origem</FieldLabel>
              <FieldValue>
                <button type="button" onClick={() => navigate(`/orcamentos/${p.orcamento_origem}`)}
                  style={{ background: "none", border: 0, padding: 0, color: "inherit", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>
                  {p.orcamento_origem_numero}
                </button>
              </FieldValue>
            </FieldRow>
            <FieldRow><FieldLabel>Valor</FieldLabel><FieldValue><strong>{fmtMoeda(p.valor_total)}</strong></FieldValue></FieldRow>
          </Block>

          <Block>
            <SectionTitle>Cliente</SectionTitle>
            <FieldRow>
              <FieldLabel>Cliente</FieldLabel>
              <FieldValue>
                <button type="button" onClick={() => navigate(`/clientes/${p.cliente}`)}
                  style={{ background: "none", border: 0, padding: 0, color: "inherit", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>
                  {p.cliente_nome}
                </button>
              </FieldValue>
            </FieldRow>
            {cab?.cliente_email && <FieldRow><FieldLabel>Email</FieldLabel><FieldValue className="mono">{cab.cliente_email}</FieldValue></FieldRow>}
            {p.vendedor_nome && <FieldRow><FieldLabel>Vendedor</FieldLabel><FieldValue>{p.vendedor_nome}</FieldValue></FieldRow>}
          </Block>

          <Block>
            <SectionTitle>Datas</SectionTitle>
            <FieldRow><FieldLabel>Criado em</FieldLabel><FieldValue>{fmtData(p.created_at, true)}</FieldValue></FieldRow>
            {p.data_entrega_prevista && <FieldRow><FieldLabel>Entrega prevista</FieldLabel><FieldValue>{fmtData(p.data_entrega_prevista)}</FieldValue></FieldRow>}
            {p.iniciado_producao_em && <FieldRow><FieldLabel>Produção</FieldLabel><FieldValue>{fmtData(p.iniciado_producao_em, true)}</FieldValue></FieldRow>}
            {p.iniciado_expedicao_em && <FieldRow><FieldLabel>Expedição</FieldLabel><FieldValue>{fmtData(p.iniciado_expedicao_em, true)}</FieldValue></FieldRow>}
            {p.entregue_em && <FieldRow><FieldLabel>Entregue</FieldLabel><FieldValue>{fmtData(p.entregue_em, true)}</FieldValue></FieldRow>}
            {p.cancelado_em && <FieldRow><FieldLabel>Cancelado</FieldLabel><FieldValue>{fmtData(p.cancelado_em, true)}</FieldValue></FieldRow>}
          </Block>

          {revisoes.length > 0 && (
            <Block>
              <SectionTitle>Histórico de revisões ({revisoes.length})</SectionTitle>
              {revisoes.slice().sort((a, b) => b.numero_revisao - a.numero_revisao).map((r) => (
                <FieldRow key={r.id}>
                  <FieldLabel>r{r.numero_revisao}</FieldLabel>
                  <FieldValue>
                    <div>{r.motivo}</div>
                    <InlineHint>
                      {r.esta_em_andamento ? <Pill $tone="warning">em andamento</Pill>
                        : <>{fmtData(r.finalizada_em, true)} · delta: {fmtMoeda(r.delta_valor)}</>}
                    </InlineHint>
                  </FieldValue>
                </FieldRow>
              ))}
            </Block>
          )}

          {p.observacoes && (
            <Block>
              <SectionTitle>Observações</SectionTitle>
              <FieldValue style={{ whiteSpace: "pre-wrap" }}>{p.observacoes}</FieldValue>
            </Block>
          )}
        </div>
      </div>

      {/* SlideOverPanel — confirmação de ações (preservado) */}
      <SlideOverPanel
        open={Boolean(confirmando)}
        onClose={fechar}
        title={
          confirmando === "iniciar-producao" ? "Iniciar produção"
          : confirmando === "gerar-ops" ? "Gerar Ordens de Produção"
          : confirmando === "iniciar-expedicao" ? "Iniciar expedição"
          : confirmando === "marcar-entregue" ? "Marcar entregue"
          : confirmando === "cancelar" ? "Cancelar pedido"
          : confirmando === "iniciar-revisao" ? "Iniciar revisão"
          : confirmando === "finalizar-revisao" ? "Finalizar revisão"
          : confirmando === "liberar-ops" ? "Liberar peças para o chão"
          : confirmando === "cancelar-revisao" ? "Cancelar revisão"
          : ""
        }
        subtitle={p.numero}
        width="min(90vw, 480px)"
      >
        <Form onSubmit={(e) => { e.preventDefault(); executar(); }}>
          {confirmando === "iniciar-producao" && (
            <p style={{ fontSize: "0.9rem" }}>Marca o pedido como <strong>EM_PRODUCAO</strong> (fallback manual).</p>
          )}
          {confirmando === "gerar-ops" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Gera <strong>1 Ordem de Produção por peça física</strong> do
                projeto. Pode ser feito uma vez só.
              </p>
              {/* M-PED-6: a escolha existia no backend e a tela nunca a
                  ofereceu — toda peça ia direto para o chão, sem momento
                  de conferência. */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                {[
                  {
                    valor: true,
                    titulo: "Liberar direto para o chão",
                    ajuda: "As peças já aparecem no terminal e o pedido entra em produção.",
                  },
                  {
                    valor: false,
                    titulo: "Gerar em rascunho (o PCP confere antes)",
                    ajuda:
                      "As peças ficam retidas até alguém liberar. O pedido "
                      + "continua como recebido. Use quando o projeto ainda "
                      + "pode mudar — depois que a chapa é cortada, não volta.",
                  },
                ].map((op) => (
                  <label
                    key={String(op.valor)}
                    style={{
                      display: "flex", gap: 10, padding: "10px 12px",
                      border: "1px solid var(--border)", borderRadius: 6,
                      cursor: "pointer",
                      background: liberarAoGerar === op.valor ? "var(--info-bg)" : "transparent",
                    }}
                  >
                    <input
                      type="radio"
                      name="liberar-ao-gerar"
                      checked={liberarAoGerar === op.valor}
                      onChange={() => setLiberarAoGerar(op.valor)}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <strong style={{ display: "block" }}>{op.titulo}</strong>
                      <span style={{ fontSize: 12, opacity: 0.75 }}>{op.ajuda}</span>
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
          {confirmando === "liberar-ops" && (
            <p style={{ fontSize: "0.9rem" }}>
              Solta para o terminal as peças que estavam retidas para
              conferência, e o pedido entra em produção. A partir daqui a
              peça pode ser cortada.
            </p>
          )}
          {confirmando === "iniciar-expedicao" && (
            <p style={{ fontSize: "0.9rem" }}>Marca como <strong>EM_EXPEDIÇÃO</strong>. Peças prontas, aguardando logística.</p>
          )}
          {confirmando === "marcar-entregue" && (
            <p style={{ fontSize: "0.9rem" }}>Marca como <strong>ENTREGUE</strong>. Terminal feliz.</p>
          )}
          {confirmando === "cancelar" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>Marca como <strong>CANCELADO</strong>. Terminal — para refazer, criar outro pedido.</p>
              <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo do cancelamento (obrigatório)" required autoFocus rows={3} />
              {/* Sem isto o chão de fábrica continuava produzindo um pedido
                  morto: o backend aceita o flag e nenhuma tela o enviava. */}
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10, fontSize: "0.85rem" }}>
                <input
                  type="checkbox"
                  checked={cancelarProducao}
                  onChange={(e) => setCancelarProducao(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  Cancelar também as peças em produção
                  <span className="tiny muted" style={{ display: "block" }}>
                    Peças já concluídas não são afetadas. Desmarque se a fábrica
                    deve terminar o que está na bancada.
                  </span>
                </span>
              </label>
            </>
          )}
          {confirmando === "iniciar-revisao" && (
            <>
              <p style={{ fontSize: "0.9rem" }}>
                Abre <strong>revisão r{(p.numero_revisao || 0) + 1}</strong>. Estado atual capturado como snapshot.
              </p>
              <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo da revisão (ex: cliente adicionou puxador)" required autoFocus rows={3} />
            </>
          )}
          {confirmando === "finalizar-revisao" && (
            <p style={{ fontSize: "0.9rem" }}>
              Consolida a revisão r{p.numero_revisao}. <strong>Mudanças viram contratuais.</strong>
            </p>
          )}
          {confirmando === "cancelar-revisao" && (
            <p style={{ fontSize: "0.9rem" }}>
              <strong>Restaura</strong> os ambientes/itens do estado anterior. O registro da revisão é deletado.
            </p>
          )}
          {acaoErro && (
            <InlineHint style={{ color: "var(--danger-text)" }}>
              <FiAlertCircle size={12} /> {acaoErro}
            </InlineHint>
          )}
          <FormFooter>
            <SecondaryButton type="button" onClick={fechar} disabled={salvando}>Cancelar</SecondaryButton>
            {confirmando === "cancelar" || confirmando === "cancelar-revisao" ? (
              <DangerButton type="submit" disabled={salvando}>{salvando ? "Aplicando..." : "Confirmar"}</DangerButton>
            ) : (
              <PrimaryButton type="submit" disabled={salvando}>{salvando ? "Aplicando..." : "Confirmar"}</PrimaryButton>
            )}
          </FormFooter>
        </Form>
      </SlideOverPanel>
    </PageContainer>
  );
}

export default PedidoDetalhe;
